import React from "react";
import { FixedSizeList } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import Chip from "../common/Chip.jsx";
import { categoryOf, folderIncludeState } from "./importHelpers";

// Match FileGridView: only virtualize large trees; small ones render plainly.
const VIRTUALIZE_THRESHOLD = 150;
const ROW_HEIGHT = 30;
const INDENT = 16; // px per depth level (matches the guide-line width)

const CATEGORY_ICON = {
    Audio: "🎵",
    Image: "🖼️",
    "3D Model": "🧊",
    Video: "🎬",
    Document: "📄",
    Archive: "🗜️",
    Other: "📄",
};

/** Checkbox that supports the indeterminate ("some") visual state. */
const TriCheckbox = ({ state, disabled, onChange }) => (
    <input
        type="checkbox"
        className="iw-row-check"
        checked={state !== "none"}
        ref={(el) => {
            if (el) el.indeterminate = state === "some";
        }}
        disabled={disabled}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => onChange(e.target.checked)}
    />
);

// Stable component for react-window rows — reads everything from itemData so its
// identity never changes (a Row defined inline in the render-prop would remount
// every visible row on each re-render).
const VirtualRow = ({ index, style, data }) => data.renderRow(data.rows[index], style);

const ImportTreeView = ({ rows, selectedKey, disabled, onToggleExpand, onToggleInclude, onSelect }) => {
    const onRowKeyDown = (e, row) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelect(row);
        } else if (e.key === "ArrowRight" && row.type === "folder" && row.hasChildren && !row.open) {
            e.preventDefault();
            onToggleExpand(row.path);
        } else if (e.key === "ArrowLeft" && row.type === "folder" && row.open) {
            e.preventDefault();
            onToggleExpand(row.path);
        }
    };

    const renderRow = (row, style) => {
        const isFolder = row.type === "folder";
        const node = row.node;
        const state = isFolder ? folderIncludeState(node) : node.include !== false ? "all" : "none";
        const ownTags = node.tags || [];

        const selected = selectedKey === row.key;

        return (
            <div
                key={row.key}
                className={`iw-row treeview-${row.type} ${selected ? "selected" : ""} ${state === "none" ? "excluded" : ""}`}
                style={style}
                onClick={() => onSelect(row)}
                onKeyDown={(e) => onRowKeyDown(e, row)}
                role="treeitem"
                tabIndex={0}
                aria-level={row.depth + 1}
                aria-selected={selected}
                aria-expanded={isFolder && row.hasChildren ? row.open : undefined}
            >
                {/* indent guide lines (one per ancestor level) */}
                <span className="iw-guides" aria-hidden="true">
                    {Array.from({ length: row.depth }, (_, i) => (
                        <span key={i} className="iw-guide" style={{ width: INDENT }} />
                    ))}
                </span>

                {isFolder && row.hasChildren ? (
                    <button
                        type="button"
                        className="iw-caret"
                        tabIndex={-1}
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleExpand(row.path);
                        }}
                        aria-label={row.open ? "Collapse" : "Expand"}
                    >
                        <span className={`iw-caret-tri ${row.open ? "open" : ""}`}>▶</span>
                    </button>
                ) : (
                    <span className="iw-caret" />
                )}

                <TriCheckbox state={state} disabled={disabled} onChange={(v) => onToggleInclude(row, v)} />

                <span className="iw-row-icon">
                    {isFolder ? (row.open ? "📂" : "📁") : CATEGORY_ICON[categoryOf(row.name)]}
                </span>
                <span className="iw-row-name" title={row.name}>
                    {row.name}
                </span>

                <span className="iw-row-tags">
                    {ownTags.slice(0, 3).map((t) => (
                        <Chip key={t} size="sm" className="iw-chip-mini">
                            {t}
                        </Chip>
                    ))}
                    {ownTags.length > 3 && <span className="iw-tag-more">+{ownTags.length - 3}</span>}
                    {(node.suggestedTags || []).length > 0 && (
                        <span className="iw-suggest-badge" title={`${node.suggestedTags.length} AI suggestion(s)`}>
                            ✨{node.suggestedTags.length}
                        </span>
                    )}
                    {row.inheritedTags && row.inheritedTags.length > 0 && (
                        <span className="iw-inherit-badge" title={`Inherits: ${row.inheritedTags.join(", ")}`}>
                            ↳{row.inheritedTags.length}
                        </span>
                    )}
                </span>
            </div>
        );
    };

    if (rows.length === 0) {
        return <div className="iw-tree iw-tree-empty">No files match the current filters.</div>;
    }

    if (rows.length <= VIRTUALIZE_THRESHOLD) {
        return (
            <div className="iw-tree" role="tree">
                {rows.map((row) => renderRow(row, {}))}
            </div>
        );
    }

    return (
        <div className="iw-tree iw-tree-virtual" role="tree">
            <AutoSizer>
                {({ height, width }) => {
                    if (height < 1 || width < 1) {
                        return <div>{rows.map((row) => renderRow(row, {}))}</div>;
                    }
                    return (
                        <FixedSizeList
                            height={height}
                            width={width}
                            itemCount={rows.length}
                            itemSize={ROW_HEIGHT}
                            itemKey={(index) => rows[index].key}
                            itemData={{ rows, renderRow }}
                        >
                            {VirtualRow}
                        </FixedSizeList>
                    );
                }}
            </AutoSizer>
        </div>
    );
};

export default ImportTreeView;
